import {
  convertStockQuantity,
  DEFAULT_STOCK_UNIT,
  normalizeStockUnit,
} from "./stockUnits";
import { buildOperationalConsumptions } from "./orderModel";
import {
  consumeBranchIngredientStock,
  listBranchIngredientStock,
  restoreBranchIngredientStock,
  saveBranchIngredientStock,
} from "../supabase/branchStockService";

const DEFAULT_STOCK_KEY = "mozzoapp:ingredient-stock";
export const INGREDIENT_STOCK_UPDATED_EVENT = "mozzoapp:ingredient-stock-updated";

export const buildIngredientStockStorageKey = (restaurantId) =>
  `${DEFAULT_STOCK_KEY}:${restaurantId || "default"}`;

const normalizeIngredientName = (value = "") => value.trim().toLowerCase();

const toSafeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const resolveIngredientIdFromConsumption = (
  consumo,
  ingredientes = []
) => {
  if (consumo?.id || consumo?.ingredientId) {
    return consumo?.id || consumo?.ingredientId || "";
  }

  const consumptionName = normalizeIngredientName(
    consumo?.nombre || consumo?.ingredientName || ""
  );

  if (!consumptionName) {
    return "";
  }

  return (
    ingredientes.find(
      (ingrediente) =>
        normalizeIngredientName(ingrediente.nombre) === consumptionName
    )?.id || ""
  );
};

export const getIngredientStock = (restaurant) => {
  try {
    const key = buildIngredientStockStorageKey(restaurant?.id);
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.error("No pudimos leer el stock local:", error);
    return {};
  }
};

const persistLocalIngredientStock = (restaurant, stockMap) => {
  try {
    const key = buildIngredientStockStorageKey(restaurant?.id);
    window.localStorage.setItem(key, JSON.stringify(stockMap));
    window.dispatchEvent(
      new CustomEvent(INGREDIENT_STOCK_UPDATED_EVENT, {
        detail: {
          restaurantId: restaurant?.id || "default",
          stockMap,
        },
      })
    );
  } catch (error) {
    console.error("No pudimos guardar el stock local:", error);
  }
};

export const getIngredientStockChanges = (stockMap = {}, baseline = {}) =>
  Object.fromEntries(Object.entries(stockMap).filter(([id, record]) => {
    const previous = baseline[id];
    return !previous ||
      String(record?.cantidad ?? record?.quantity ?? "") !== String(previous?.cantidad ?? previous?.quantity ?? "") ||
      normalizeStockUnit(record?.unidad || record?.unit) !== normalizeStockUnit(previous?.unidad || previous?.unit);
  }));

export const saveIngredientStock = (restaurant, stockMap, { baseline } = {}) => {
  // Save only edited ingredients so an unrelated order consumption is not reset.
  const changes = baseline ? getIngredientStockChanges(stockMap, baseline) : stockMap;
  if (!Object.keys(changes || {}).length) return Promise.resolve({ ok: true, stockMap: {} });

  if (!restaurant?.id) {
    persistLocalIngredientStock(restaurant, { ...getIngredientStock(restaurant), ...changes });
    return Promise.resolve({
      ok: true,
      stockMap: changes,
      skipped: true,
      reason: "missing_branch_id",
    });
  }

  return saveBranchIngredientStock(restaurant.id, changes)
    .then((result) => {
      if (result?.ok === true) {
        persistLocalIngredientStock(restaurant, {
          ...getIngredientStock(restaurant), ...result.stockMap,
        });
      }
      return result || { ok: false, error: "No recibimos confirmacion del guardado." };
    })
    .catch((error) => {
      console.warn("No pudimos espejar el stock en Supabase:", error);
      return {
        ok: false,
        stockMap,
        code: error?.code,
        error:
          error instanceof Error
            ? error.message
            : "No pudimos espejar el stock en Supabase.",
      };
    });
};

export const hydrateIngredientStockFromRemote = async (restaurant, { throwOnError = false } = {}) => {
  if (!restaurant?.id) {
    return getIngredientStock(restaurant);
  }

  try {
    const remoteStock = await listBranchIngredientStock(restaurant.id);

    if (remoteStock && typeof remoteStock === "object") {
      persistLocalIngredientStock(restaurant, remoteStock);
      return remoteStock;
    }
  } catch (error) {
    if (throwOnError) throw error;
    console.warn("No pudimos sincronizar el stock remoto de la sucursal:", error);
  }

  return getIngredientStock(restaurant);
};

export const validateIngredientStock = (
  restaurant,
  consumos = [],
  {
    ingredientes = [],
    stockMap = null,
    allowMissingStockRecords = true,
  } = {}
) => {
  const currentStock = stockMap || getIngredientStock(restaurant);
  const aggregatedByIngredient = new Map();
  const unresolved = [];
  const incompatibleUnits = [];

  consumos.forEach((consumo) => {
    const quantity = Number(consumo?.cantidad ?? consumo?.quantity ?? 0);

    if (quantity <= 0) {
      return;
    }

    const ingredientId = resolveIngredientIdFromConsumption(consumo, ingredientes);

    if (!ingredientId) {
      unresolved.push(consumo);
      return;
    }

    const existingRecord = currentStock?.[ingredientId] || null;

    if (!existingRecord) {
      const previousEntry = aggregatedByIngredient.get(ingredientId);
      aggregatedByIngredient.set(ingredientId, {
        ingredientId,
        ingredientName:
          consumo?.ingredientName ||
          consumo?.nombre ||
          previousEntry?.ingredientName ||
          "Ingrediente",
        requestedQuantity:
          Number(previousEntry?.requestedQuantity || 0) + quantity,
        requestedUnit:
          consumo?.unit ||
          consumo?.unidad ||
          previousEntry?.requestedUnit ||
          DEFAULT_STOCK_UNIT,
        stockUnit:
          previousEntry?.stockUnit ||
          normalizeStockUnit(
            consumo?.unit || consumo?.unidad || DEFAULT_STOCK_UNIT
          ),
        availableQuantity: null,
        missingStockRecord: true,
      });
      return;
    }

    const stockUnit = normalizeStockUnit(
      existingRecord.unidad || existingRecord.unit || DEFAULT_STOCK_UNIT
    );
    const consumptionUnit = normalizeStockUnit(
      consumo?.unit || consumo?.unidad || stockUnit
    );
    const normalizedQuantity = convertStockQuantity(
      quantity,
      consumptionUnit,
      stockUnit
    );

    if (normalizedQuantity === null) {
      incompatibleUnits.push({
        ingredientId,
        ingredientName:
          consumo?.ingredientName || consumo?.nombre || "Ingrediente",
        requestedQuantity: quantity,
        requestedUnit: consumptionUnit,
        stockUnit,
      });
      return;
    }

    const previousEntry = aggregatedByIngredient.get(ingredientId);
    aggregatedByIngredient.set(ingredientId, {
      ingredientId,
      ingredientName:
        consumo?.ingredientName ||
        consumo?.nombre ||
        previousEntry?.ingredientName ||
        "Ingrediente",
      requestedQuantity:
        Number(previousEntry?.requestedQuantity || 0) + normalizedQuantity,
      requestedUnit: stockUnit,
      stockUnit,
      availableQuantity: toSafeNumber(
        existingRecord.cantidad ?? existingRecord.quantity,
        0
      ),
      missingStockRecord: false,
    });
  });

  const requested = Array.from(aggregatedByIngredient.values());
  const missingStockRecords = requested.filter((entry) => entry.missingStockRecord);
  const insufficient = requested.filter((entry) => {
    if (entry.missingStockRecord) {
      return !allowMissingStockRecords;
    }

    return Number(entry.availableQuantity || 0) < Number(entry.requestedQuantity || 0);
  });

  return {
    ok: insufficient.length === 0 && incompatibleUnits.length === 0,
    requested,
    insufficient,
    missingStockRecords,
    incompatibleUnits,
    unresolved,
  };
};

const buildOkValidationResult = () => ({
  ok: true,
  requested: [],
  insufficient: [],
  missingStockRecords: [],
  incompatibleUnits: [],
  unresolved: [],
});

const formatIngredientIssueList = (issues = []) => {
  const uniqueNames = Array.from(
    new Set(
      issues
        .map((issue) => String(issue?.ingredientName || "").trim())
        .filter(Boolean)
    )
  );

  if (uniqueNames.length === 0) {
    return "los ingredientes necesarios";
  }

  if (uniqueNames.length === 1) {
    return uniqueNames[0];
  }

  if (uniqueNames.length === 2) {
    return `${uniqueNames[0]} y ${uniqueNames[1]}`;
  }

  return `${uniqueNames.slice(0, 2).join(", ")} y ${uniqueNames.length - 2} mas`;
};

export const getIngredientStockValidationMessage = (
  validation,
  {
    unavailablePrefix = "No hay stock suficiente de",
    incompatiblePrefix = "Hay ingredientes con unidades de stock incompatibles",
    fallbackMessage = "No hay stock suficiente para continuar con el pedido.",
  } = {}
) => {
  if (!validation) {
    return fallbackMessage;
  }
  if (validation.selectionError) return validation.selectionError;

  if (Array.isArray(validation.insufficient) && validation.insufficient.length > 0) {
    return `${unavailablePrefix} ${formatIngredientIssueList(validation.insufficient)}.`;
  }

  if (
    Array.isArray(validation.incompatibleUnits) &&
    validation.incompatibleUnits.length > 0
  ) {
    return `${incompatiblePrefix}: ${formatIngredientIssueList(
      validation.incompatibleUnits
    )}.`;
  }

  return fallbackMessage;
};

export const getOperationalProductStockState = (
  restaurant,
  product,
  {
    ingredientes = [],
    stockMap = null,
    stockEnabled = true,
    quantity = 1,
    selection = null,
    allowMissingStockRecords = false,
    unavailablePrefix = "Sin stock suficiente de",
    incompatiblePrefix = "Hay ingredientes con unidades de stock incompatibles",
    fallbackMessage = "Este producto no tiene stock suficiente para venderse ahora.",
  } = {}
) => {
  if (!stockEnabled) {
    return {
      available: true,
      reason: "",
      validation: buildOkValidationResult(),
      hasRecipe: false,
    };
  }

  const consumos = buildOperationalConsumptions(product, {
    selection,
    quantity,
  });

  if (!consumos.length) {
    return {
      available: true,
      reason: "",
      validation: buildOkValidationResult(),
      hasRecipe: false,
    };
  }

  const validation = validateIngredientStock(restaurant, consumos, {
    ingredientes,
    stockMap,
    allowMissingStockRecords,
  });
  const available = validation?.ok !== false;

  return {
    available,
    validation,
    hasRecipe: true,
    reason: available
      ? ""
      : getIngredientStockValidationMessage(validation, {
          unavailablePrefix,
          incompatiblePrefix,
          fallbackMessage,
        }),
  };
};

export const consumeIngredientStock = (restaurant, ingredientes = [], consumos = []) => {
  const currentStock = getIngredientStock(restaurant);
  const nextStock = { ...currentStock };
  const applied = [];
  const unresolved = [];

  consumos.forEach((consumo) => {
    const quantity = Number(consumo?.cantidad ?? consumo?.quantity ?? 0);
    if (quantity <= 0) {
      return;
    }

    const ingredientId =
      consumo?.id ||
      consumo?.ingredientId ||
      ingredientes.find(
        (ingrediente) =>
          normalizeIngredientName(ingrediente.nombre) ===
          normalizeIngredientName(consumo?.nombre || consumo?.ingredientName || "")
      )?.id;

    if (!ingredientId) {
      unresolved.push(consumo);
      return;
    }

    const previousRecord = nextStock[ingredientId] || {
      unidad: DEFAULT_STOCK_UNIT,
      cantidad: "0",
    };
    const stockUnit = normalizeStockUnit(
      previousRecord.unidad || previousRecord.unit || DEFAULT_STOCK_UNIT
    );
    const consumptionUnit = normalizeStockUnit(
      consumo?.unit || consumo?.unidad || stockUnit
    );
    const normalizedQuantity = convertStockQuantity(
      quantity,
      consumptionUnit,
      stockUnit
    );

    if (normalizedQuantity === null) {
      unresolved.push({
        ...consumo,
        ingredientId,
        stockUnit,
      });
      return;
    }

    const currentValue = Number(previousRecord.cantidad || 0);
    const nextValue = Math.max(0, currentValue - normalizedQuantity);

    nextStock[ingredientId] = {
      ...previousRecord,
      unidad: stockUnit,
      cantidad: String(nextValue),
      updatedAt: new Date().toISOString(),
    };

    applied.push({
      ingredientId,
      quantity: normalizedQuantity,
      requestedQuantity: quantity,
      unit: stockUnit,
      requestedUnit: consumptionUnit,
      });
  });

  persistLocalIngredientStock(restaurant, nextStock);

  if (restaurant?.id && applied.length > 0) {
    void consumeBranchIngredientStock(restaurant.id, ingredientes, consumos, {
      referenceType: "operational_order",
      notes: "Descuento automatico desde el flujo operativo autenticado.",
    })
      .then((remoteResult) => {
        if (remoteResult?.ok && remoteResult.stockMap) {
          const mergedStockMap = {
            ...nextStock,
            ...remoteResult.stockMap,
          };

          persistLocalIngredientStock(restaurant, mergedStockMap);
        }
      })
      .catch((error) => {
        console.warn("No pudimos espejar el descuento de stock en Supabase:", error);
      });
  }

  return {
    stockMap: nextStock,
    applied,
    unresolved,
  };
};

export const restoreIngredientStock = (restaurant, ingredientes = [], consumos = []) => {
  const currentStock = getIngredientStock(restaurant);
  const nextStock = { ...currentStock };
  const applied = [];
  const unresolved = [];

  consumos.forEach((consumo) => {
    const quantity = Number(consumo?.cantidad ?? consumo?.quantity ?? 0);
    if (quantity <= 0) {
      return;
    }

    const ingredientId =
      consumo?.id ||
      consumo?.ingredientId ||
      ingredientes.find(
        (ingrediente) =>
          normalizeIngredientName(ingrediente.nombre) ===
          normalizeIngredientName(consumo?.nombre || consumo?.ingredientName || "")
      )?.id;

    if (!ingredientId) {
      unresolved.push(consumo);
      return;
    }

    const previousRecord = nextStock[ingredientId] || {
      unidad: DEFAULT_STOCK_UNIT,
      cantidad: "0",
    };
    const stockUnit = normalizeStockUnit(
      previousRecord.unidad || previousRecord.unit || DEFAULT_STOCK_UNIT
    );
    const consumptionUnit = normalizeStockUnit(
      consumo?.unit || consumo?.unidad || stockUnit
    );
    const normalizedQuantity = convertStockQuantity(
      quantity,
      consumptionUnit,
      stockUnit
    );

    if (normalizedQuantity === null) {
      unresolved.push({
        ...consumo,
        ingredientId,
        stockUnit,
      });
      return;
    }

    const currentValue = Number(previousRecord.cantidad || 0);
    const nextValue = Math.max(0, currentValue + normalizedQuantity);

    nextStock[ingredientId] = {
      ...previousRecord,
      unidad: stockUnit,
      cantidad: String(nextValue),
      updatedAt: new Date().toISOString(),
    };

    applied.push({
      ingredientId,
      quantity: normalizedQuantity,
      requestedQuantity: quantity,
      unit: stockUnit,
      requestedUnit: consumptionUnit,
    });
  });

  persistLocalIngredientStock(restaurant, nextStock);

  if (restaurant?.id && applied.length > 0) {
    void restoreBranchIngredientStock(restaurant.id, ingredientes, consumos, {
      referenceType: "operational_order_restore",
      notes: "Reposicion automatica desde el flujo operativo autenticado.",
    })
      .then((remoteResult) => {
        if (remoteResult?.ok && remoteResult.stockMap) {
          const mergedStockMap = {
            ...nextStock,
            ...remoteResult.stockMap,
          };

          persistLocalIngredientStock(restaurant, mergedStockMap);
        }
      })
      .catch((error) => {
        console.warn("No pudimos espejar la reposicion de stock en Supabase:", error);
      });
  }

  return {
    stockMap: nextStock,
    applied,
    unresolved,
  };
};
